import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { API, StatCard, Field, inputCls, selectCls, Building2, DollarSign, TrendingUp, CreditCard, Plus, Check } from './shared';

export default function InstitutionalTab() {
  const [instStats, setInstStats] = useState(null);
  const [showOnboard, setShowOnboard] = useState(false);
  const [obName, setObName] = useState('');
  const [obEmail, setObEmail] = useState('');
  const [obAddr, setObAddr] = useState('');
  const [obTier, setObTier] = useState('professional');
  const [obDeposit, setObDeposit] = useState('');
  const [obLoading, setObLoading] = useState(false);
  const [obResult, setObResult] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/agent-credit/institutional/stats`).then(r => r.json()).then(setInstStats).catch(() => {});
  }, []);

  const onboard = async (e) => {
    e.preventDefault();
    setObLoading(true); setObResult(null);
    try {
      const res = await fetch(`${API}/api/agent-credit/institutional/onboard`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ institution_name: obName.trim(), contact_email: obEmail.trim(), lender_address: obAddr.trim(), tier: obTier, initial_deposit: parseFloat(obDeposit) || 0 }),
      });
      const data = await res.json();
      setObResult(res.ok ? { type: 'success', data } : { type: 'error', msg: data.detail });
    } catch { setObResult({ type: 'error', msg: 'Network error' }); }
    finally { setObLoading(false); }
  };

  const tiers = [
    { id: 'standard', name: 'Standard', fee: '$55/mo', maxDep: '$100K', priority: false, rateLimit: '100 req/min' },
    { id: 'professional', name: 'Professional', fee: '$500/mo', maxDep: '$1M', priority: true, rateLimit: '1,000 req/min' },
    { id: 'enterprise', name: 'Enterprise', fee: '$5,000/mo', maxDep: '$50M', priority: true, rateLimit: '10,000 req/min' },
  ];

  return (
    <div data-testid="institutional-tab">
      {instStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Building2} label="Institutions" value={instStats.total_institutions} color="blue" />
          <StatCard icon={DollarSign} label="Capital Deployed" value={`$${(instStats.total_institutional_capital || 0).toLocaleString()}`} color="green" />
          <StatCard icon={TrendingUp} label="Institutional Yield" value={`$${(instStats.total_institutional_yield || 0).toFixed(2)}`} color="purple" />
          <StatCard icon={CreditCard} label="Subscription Rev" value={`$${(instStats.subscription_revenue_monthly || 0).toLocaleString()}/mo`} color="orange" />
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {tiers.map(t => (
          <div key={t.id} className={`rounded-2xl border p-5 transition-all ${t.id === 'enterprise' ? 'border-orange-500/30 bg-orange-500/[0.03]' : t.id === 'professional' ? 'border-blue-500/30 bg-blue-500/[0.03]' : 'border-white/10 bg-white/[0.02]'}`}>
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">{t.name}</div>
            <div className="text-2xl font-mono font-bold text-white mb-3">{t.fee}</div>
            <div className="space-y-2 text-xs text-gray-400">
              <div className="flex justify-between"><span>Max Deposit</span><span className="font-mono">{t.maxDep}</span></div>
              <div className="flex justify-between"><span>Priority Allocation</span><span>{t.priority ? <Check className="w-3 h-3 text-green-400 inline" /> : '—'}</span></div>
              <div className="flex justify-between"><span>API Rate</span><span className="font-mono">{t.rateLimit}</span></div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end mb-4">
        <Button onClick={() => setShowOnboard(!showOnboard)} className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:brightness-110" data-testid="inst-onboard-btn">
          <Plus className="w-4 h-4 mr-2" /> Onboard Institution
        </Button>
      </div>

      <AnimatePresence>
        {showOnboard && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-8 overflow-hidden">
            <div className="rounded-2xl border border-blue-500/20 bg-blue-500/[0.03] p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><Building2 className="w-5 h-5 text-blue-400" /> Institutional Onboarding</h3>
              <form onSubmit={onboard} className="grid md:grid-cols-2 gap-4">
                <Field label="Institution Name"><input value={obName} onChange={e => setObName(e.target.value)} placeholder="e.g. Paradigm Capital" className={inputCls.replace('font-mono','')} required data-testid="inst-name" /></Field>
                <Field label="Contact Email"><input value={obEmail} onChange={e => setObEmail(e.target.value)} placeholder="ops@fund.com" type="email" className={inputCls.replace('font-mono','')} required data-testid="inst-email" /></Field>
                <Field label="Treasury Address"><input value={obAddr} onChange={e => setObAddr(e.target.value)} placeholder="0x..." className={inputCls} required data-testid="inst-address" /></Field>
                <Field label="Subscription Tier">
                  <select value={obTier} onChange={e => setObTier(e.target.value)} className={selectCls} data-testid="inst-tier">
                    <option value="standard">Standard ($55/mo)</option>
                    <option value="professional">Professional ($500/mo)</option>
                    <option value="enterprise">Enterprise ($5,000/mo)</option>
                  </select>
                </Field>
                <Field label="Initial Deposit (USD)"><input value={obDeposit} onChange={e => setObDeposit(e.target.value)} placeholder="100000" type="number" min="0" className={inputCls} data-testid="inst-deposit" /></Field>
                <div className="flex items-end">
                  <Button type="submit" disabled={obLoading} className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600" data-testid="inst-submit-btn">
                    {obLoading ? 'Processing...' : 'Onboard Institution'}
                  </Button>
                </div>
              </form>
              {obResult && (
                <div className={`mt-4 p-4 rounded-lg border text-sm ${obResult.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`} data-testid="inst-result">
                  {obResult.type === 'success' ? (
                    <div><p className="flex items-center gap-2"><Check className="w-4 h-4" /> {obResult.data.message}</p>
                      <p className="text-xs text-gray-500 mt-2 font-mono">API Key: {obResult.data.api_key}</p>
                      <p className="text-xs text-red-400 mt-1">Save this key — it won't be shown again.</p></div>
                  ) : (<p>{obResult.msg}</p>)}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
